import { generate as generateShortUuid } from "short-uuid";

import type { ArgumentTreeType } from "#/entities/argument/model/types";
import type { CallstackHelper } from "#/features/callstack/model/callstackSlice";

export interface NodeMeta {
  id: string;
  type: ArgumentTreeType;
  color?: string | null;
  animation?: string | null;
  colorMap?: Record<number | string, string>;
  info?: Record<string, any>;
}

export abstract class NodeBase<T extends number | string> {
  readonly meta!: NodeMeta;
  protected name!: string;
  protected readonly callstack!: CallstackHelper;

  protected constructor(
    val: T,
    meta: NodeMeta,
    name: string,
    callstack: CallstackHelper,
  ) {
    Object.defineProperties(this, {
      _val: {
        value: val,
        enumerable: false,
        writable: true,
      },
      meta: {
        value: meta,
        enumerable: false,
      },
      name: {
        value: name,
        enumerable: false,
        writable: true,
      },
      callstack: {
        value: callstack,
        enumerable: false,
      },
    });
  }

  _val!: T;

  public get val(): T {
    if (globalThis.recordReads !== false) {
      this.blink();
    }

    return this._val;
  }

  public set val(value: T) {
    const prevVal = this._val;
    this._val = value;
    this.callstack.addOne({
      ...this.getDispatchBase(),
      name: "setVal",
      args: { value },
      prevArgs: { value: prevVal },
    });
  }

  public setColor(color: string | null, animation?: string) {
    const prevColor = this.meta.color;
    const prevAnimation = this.meta.animation;
    this.meta.color = color;
    this.meta.animation = animation;
    this.callstack.addOne({
      ...this.getDispatchBase(),
      name: "setColor",
      args: { color, animation },
      prevArgs: { color: prevColor, animation: prevAnimation },
    });
  }

  public setColorMap(colorMap: Record<T, string>) {
    const prevColorMap = this.meta.colorMap;
    this.meta.colorMap = colorMap;
    this.callstack.addOne({
      ...this.getDispatchBase(),
      name: "setColorMap",
      args: { colorMap },
      prevArgs: { colorMap: prevColorMap },
    });
  }

  public blink() {
    const base = this.getDispatchBase();
    const prevFrame = this.callstack.frames.at(-1);
    if (prevFrame?.name === "blink" && prevFrame.nodeId === base.nodeId) return;

    this.callstack.addOne({
      ...base,
      name: "blink",
    });
  }

  public setInfo(info: Record<string, any>) {
    const prevInfo = this.meta.info;
    this.meta.info = info;
    this.callstack.addOne({
      ...this.getDispatchBase(),
      name: "setInfo",
      args: { info },
      prevArgs: { info: prevInfo },
    });
  }

  protected getDispatchBase() {
    return {
      id: generateShortUuid(),
      argType: this.meta.type,
      treeName: this.name,
      structureType: "treeNode",
      nodeId: this.meta.id,
      timestamp: performance.now(),
    } as const;
  }
}
